import './main-layout.css';

import { useStore } from '@nanostores/react';
import { useLocation } from '@reach/router';
import {
	enableLinkClickTracking,
	LinkClickTrackingPlugin,
} from '@snowplow/browser-plugin-link-click-tracking';
import {
	enableActivityTracking,
	newTracker,
	trackPageView,
} from '@snowplow/browser-tracker';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { $moduleAbTest } from '../stores/module-ab-test';
import { $navAbTest } from '../stores/nav-ab-test';
import { $pageAbTest } from '../stores/page-ab-test';
import { Footer } from '../components/footer';
import { NavAbTest } from '../components/nav-ab-test';
import { Navbar } from '../components/navbar';
import {
	initDreamData,
	initDreamDataCL,
	initMarketoMunchkin,
	initMarketoMunchkinWithCP,
} from '../utils/third-party-scripts';
import { utmForwarding } from '../utils/utm-forwarding';

// initiate snowplow tracker
newTracker('sp-web', 'https://analytics.helloalma.com', {
	appId: 'prod',
	plugins: [LinkClickTrackingPlugin()],
	postPath: '/8b672/eu4',
	crossDomainLinker: function (linkElement) {
		// do not append cross domain linker to mailto links
		if (linkElement.href.startsWith('mailto:')) {
			return false;
		}

		// do not append cross domain linker to links that are on the same domain
		if (linkElement.hostname === 'helloalma.com') {
			return false;
		}

		return true;
	},
});

// reveal elements on scroll
function revealSiblings(element: Element, sequential = false) {
	if (element.parentElement) {
		const siblings = Array.from(element.parentElement.children);

		if (sequential) {
			siblings.forEach(function (sibling, index) {
				setTimeout(function () {
					sibling.classList.add('active');
					// Adjust this value for desired delay between elements
				}, index * 250);
			});
		} else {
			siblings.forEach(function (sibling) {
				sibling.classList.add('active');
			});
		}
	}
}

function handleScroll() {
	const revealElements = document.querySelectorAll('.reveal');

	revealElements.forEach(function (revealElement) {
		const elementTop = revealElement.getBoundingClientRect().top;
		const windowHeight = window.innerHeight;
		const elementVisible = 250;
		const parentElement = revealElement.parentElement;

		// Check if the parent container has a specific class to determine reveal mode
		const revealSequentially =
			parentElement?.classList.contains('fade-sequential');

		const images = Array.from(revealElement.querySelectorAll('img'));
		images.every(function (img) {
			return img.complete && img.naturalHeight !== 0;
		});

		if (elementTop < windowHeight - elementVisible) {
			revealSiblings(revealElement, revealSequentially);
		}
	});
}

interface MainLayoutProps {
	backgroundColor?: string;
	children: React.ReactNode;
	enableChiliPiper?: boolean;
	footerContent?: any;
	hasMarketoForm?: boolean;
	navContent?: any;
	navTransparency?: string;
	route?: string;
	utmParams?: string;
}

export function MainLayout({
	backgroundColor,
	children,
	enableChiliPiper,
	footerContent = { footerItems: [] },
	hasMarketoForm,
	navContent,
	navTransparency,
	route = '/',
	utmParams,
}: MainLayoutProps) {
	// stores
	const moduleAbTest = useStore($moduleAbTest);
	const navAbTest = useStore($navAbTest);
	const pageAbTest = useStore($pageAbTest);

	// hooks
	const location = useLocation();

	// state
	const [cookiesEnabled, setCookiesEnabled] = useState(false);

	// enable snowplow activity tracking
	enableActivityTracking({
		minimumVisitLength: 1800,
		heartbeatDelay: 10,
	});

	// enable snowplow link click tracking
	enableLinkClickTracking({
		pseudoClicks: true,
	});

	// track pages views on route change using snowplow trackPageView();
	useEffect(() => {
		// Add a small delay to ensure store updates have completed
		const timeoutId = setTimeout(() => {
			console.log('Snowplow Track Pageview Data: ', {
				pageAbTest,
				moduleAbTest,
				navAbTest,
			});

			trackPageView({
				context:
					pageAbTest?.active ||
					moduleAbTest?.active ||
					navAbTest?.active
						? [
								{
									schema: 'iglu:com.helloalma/launch_darkly/jsonschema/2-0-0',
									data: {
										flag_name: pageAbTest?.active
											? pageAbTest?.flagName
											: moduleAbTest?.active
												? moduleAbTest?.flagName
												: navAbTest?.active
													? navAbTest?.flagName
													: null,
										flag_value: pageAbTest?.active
											? pageAbTest?.flagValue
											: moduleAbTest?.active
												? moduleAbTest?.flagValue
												: navAbTest?.active
													? navAbTest?.flagValue
													: null,
									},
								},
							]
						: [],
			});
		}, 1000); // 1s delay

		return () => clearTimeout(timeoutId);
	}, [route, pageAbTest, moduleAbTest, navAbTest]);

	// manually track page view on route change using marketo munchkin
	useEffect(() => {
		if (window && window.Munchkin) {
			Munchkin.munchkinFunction('visitWebPage', {
				url: location.pathname,
			});
		}
	}, [location.pathname]);

	// if utmParams are passed in through the cms, add them to the url
	useEffect(() => {
		if (utmParams) {
			console.log('Manually adding utm params to url: ', utmParams);

			const url = new URL(window.location.href);
			window.history.replaceState({}, '', `${url.pathname}?${utmParams}`);
		}
	}, [utmParams]);

	// when page is loaded, check url for the utm param _sp, if it is present - strip it from the url
	// this is to prevent the _sp param from being shared among providers and causing attribution issues
	useEffect(() => {
		const url = new URL(window.location.href);
		const spParam = url.searchParams.get('_sp');

		if (spParam) {
			console.log('Stripping _sp param from url');
			url.searchParams.delete('_sp');
			window.history.replaceState({}, '', url);
		}
	}, []);

	// check if cookies are enabled
	useEffect(() => {
		let cookieEnabled = navigator.cookieEnabled;
		if (!cookieEnabled) {
			document.cookie = 'testcookie';
			cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
		}

		if (cookieEnabled) {
			setCookiesEnabled(cookieEnabled);
		}
	}, []);

	// setup utm forwarding
	useEffect(() => {
		// check if cookies are enabled
		if (cookiesEnabled) {
			utmForwarding({
				expireMinutes: 30,
			});
		}
	}, [cookiesEnabled]);

	useEffect(() => {
		handleScroll();
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const navModule = (navContent: any) => {
		const navData = navContent?.navMenu;

		switch (navContent._type) {
			case 'navigationMenuContainer':
				return (
					<Navbar
						navContent={navData}
						navTransparency={navTransparency}
					/>
				);
			case 'navAbTest':
				return (
					<NavAbTest
						navTransparency={navTransparency}
						{...navContent}
					/>
				);
			default:
				return null;
		}
	};

	return (
		<div
			className={classNames(
				backgroundColor && `b${backgroundColor}`,
				'rel'
			)}
		>
			{/* add marketo / chili piper script */}
			<Helmet>
				{hasMarketoForm && enableChiliPiper ? (
					<script>{initMarketoMunchkinWithCP}</script>
				) : (
					<script>{initMarketoMunchkin}</script>
				)}
			</Helmet>
			{/* dreamdata scripts */}
			<Helmet>
				<script id='init-dreamdata'>{initDreamData}</script>
				<script id='init-dreamdata-cl'>{initDreamDataCL}</script>
			</Helmet>
			{navContent && navModule(navContent)}
			{children}
			{footerContent && <Footer footerContent={footerContent} />}
		</div>
	);
}
